import { faMagnifyingGlassPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef } from 'react';
import { Radar } from 'react-chartjs-2';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { SkillMatrixFallback } from './skill-matrix-fallback';
import { getColorScheme, getGraphOptions, getRadarChartData } from './skill-matrix-graphic.helper';
import { StyledRadarChartWrapper, StyledRadarWrapper, StyledZoomIndicator } from './skill-matrix-graphic.styles';
import type { SkillsMatrix, SkillsMatrixScore } from './skill-matrix-graphic.types';
export const SkillMatrixGraphic = ({
  isClickable = false,
  onSkillSelect,
  roleId,
  isDashboard = false,
  skillsData,
  isFetching
}: {
  isClickable?: boolean;
  onSkillSelect?: (skill: SkillsMatrixScore | undefined) => void;
  roleId?: string;
  isDashboard?: boolean;
  skillsData?: SkillsMatrix;
  isFetching: boolean;
}) => {
  const isDarkMode = useIsDarkMode();
  const currentColorSettings = getColorScheme(isDarkMode, roleId);
  const skillsScore = skillsData?.skills ?? [];
  const hoveredIndexRef = useRef<number | null>(null);
  const maxScore = Math.max(...skillsScore.map(skill => skill.score));
  const dynamicMax = Math.max(maxScore <= 40 ? maxScore + 10 : 100, 20);
  const isSegmentedMatrixEnabled = useFeatureIsOn('segmented-matrix');
  const isZoomed = !isSegmentedMatrixEnabled && maxScore <= 40;
  const handleLabelClick = (index: number) => {
    if (onSkillSelect) {
      onSkillSelect(skillsScore[index]);
    }
  };
  const graphOptions = getGraphOptions({
    currentColorSettings,
    isClickable,
    hoveredIndexRef,
    onLabelClick: isClickable ? handleLabelClick : undefined,
    dynamicMax,
    isZoomed,
    isDashboard
  });
  const radarChartData = getRadarChartData(skillsScore, currentColorSettings, roleId);
  return <div data-sentry-component="SkillMatrixGraphic" data-sentry-source-file="skill-matrix-graphic.tsx">
      <StyledRadarChartWrapper data-sentry-element="StyledRadarChartWrapper" data-sentry-source-file="skill-matrix-graphic.tsx">
        {!isFetching && skillsScore.length ? <StyledRadarWrapper $isDashboard={isDashboard}>
            <Radar aria-label="Radar Skills Matrix" data={radarChartData} options={graphOptions} />
          </StyledRadarWrapper> : <SkillMatrixFallback isLoadingChart={isFetching} fallbackText="Error Loading Skills Matrix" isDashboard={isDashboard} />}
      </StyledRadarChartWrapper>
      {!isFetching && !isDashboard && isZoomed && <StyledZoomIndicator>
          <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          200%
        </StyledZoomIndicator>}
    </div>;
};